/**
 * Enhanced Send Fax Panel Component
 * 
 * Enhanced version of SendFaxPanel with template selection, PDF viewer, and separate file upload sections.
 * Matches the design from the provided images with proper theme integration.
 * 
 * @component
 * @example
 * return <EnhancedSendFaxPanel />
 */

import React, { useState, useEffect, useRef } from "react";
import {
    Send as SendIcon,
    Close as CloseIcon,
    CloudUpload as UploadIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Remove as RemoveIcon,
    ZoomIn as ZoomInIcon,
    ZoomOut as ZoomOutIcon,
    Description as PdfIcon,
    Fullscreen as FullscreenIcon,
    FullscreenExit as FullscreenExitIcon,
} from "@mui/icons-material";
import { Box, TextField, Typography, Button, MenuItem, Select, FormControl, InputLabel, IconButton, Tooltip } from "@mui/material";
import { useTheme } from "../../ThemeContext";
import TemplateSelectionModal from "./TemplateSelectionModal";
import PdfViewer from "./PdfViewer";
import FaxAgentService from "../../services/fax-agent-service";
import { getApiBaseUrl } from "../../utils/apiClient";
import { message } from "antd";

const EnhancedSendFaxPanel = ({
    onClose,
    currentTheme,
    onSuccess,
}) => {
    const { themeMode } = useTheme();
    
    // Form data state
    const [formData, setFormData] = useState({
        toNumber: "",
        fromNumber: "",
        emailId: "",
        subject: "",
        description: "",
        patientName: "",
    });

    // File states
    const [coverPageFile, setCoverPageFile] = useState(null);
    const [templateFile, setTemplateFile] = useState(null);
    const [medicalRecordsFiles, setMedicalRecordsFiles] = useState([]);

    // PDF viewer state
    const [currentPdf, setCurrentPdf] = useState(null);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [isPdfFullScreen, setIsPdfFullScreen] = useState(false);

    // Template selection modal state
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    
    // Ref to PdfViewer container for fullscreen positioning
    const pdfViewerContainerRef = useRef(null);

    // Loading state
    const [loading, setLoading] = useState(false);
    
    // Purchased fax numbers for dropdown
    const [purchasedNumbers, setPurchasedNumbers] = useState([]);
    const [loadingNumbers, setLoadingNumbers] = useState(false);
    
    // Field validation errors
    const [fieldErrors, setFieldErrors] = useState({
        toNumber: "",
        fromNumber: "",
        patientName: "",
    });

    // File input refs
    const coverPageInputRef = useRef(null);
    const templateInputRef = useRef(null);
    const medicalRecordsInputRef = useRef(null);
    
    // PDF viewer ref for getting edited PDFs
    const pdfViewerRef = useRef(null);

    // Escape key exits PDF fullscreen
    useEffect(() => {
        if (!isPdfFullScreen) return;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') setIsPdfFullScreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isPdfFullScreen]);

    /**
     * Fetch purchased fax numbers on component mount
     */
    useEffect(() => {
        const fetchPurchasedNumbers = async () => {
            try {
                setLoadingNumbers(true);
                const response = await FaxAgentService.getPurchasedNumbers();
                // Handle the API response structure: { statusCode, response: [...], message }
                const numbers = response?.response || response?.data || response?.numbers || response || [];
                setPurchasedNumbers(Array.isArray(numbers) ? numbers : []);
            } catch (error) {
                console.error('Error fetching purchased numbers:', error);
                setPurchasedNumbers([]);
            } finally {
                setLoadingNumbers(false);
            }
        };
        
        fetchPurchasedNumbers();
    }, []);

    /**
     * Handle removing selected template
     */
    const handleRemoveTemplate = () => {
        setSelectedTemplate(null);
        setTemplateFile(null);
        setCurrentPdf(null);
        setPdfUrl(null);
    };

    /**
     * Format phone number from +19075313116 to +1 (907) 531-3116
     */
    const formatPhoneNumber = (phoneNumber) => {
        if (!phoneNumber) return '';
        
        // Remove all non-digits
        const digits = phoneNumber.replace(/\D/g, '');
        
        // Handle different lengths
        if (digits.length === 11 && digits[0] === '1') {
            // Format as +1 (XXX) XXX-XXXX
            return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
        } else if (digits.length === 10) {
            // Assume US number without country code
            return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
        }
        
        // For other formats, return as-is
        return phoneNumber;
    };

    /**
     * Validate fax number format
     * Allows: digits, spaces, dashes, parentheses, and + sign
     * Max length: 20 characters
     */
    const validateFaxNumber = (value) => {
        // Remove all whitespace for validation
        const cleaned = value.replace(/\s/g, '');
        
        // Check if empty (optional field validation happens separately)
        if (!cleaned) {
            return { isValid: true, error: "" };
        }
        
        // Check length (max 20 characters including formatting)
        if (value.length > 20) {
            return { isValid: false, error: "Fax number cannot exceed 20 characters" };
        }
        
        // Check for invalid characters - only allow digits, +, -, (, ), and spaces
        const faxNumberRegex = /^[\d\s+\-()]+$/;
        if (!faxNumberRegex.test(value)) {
            return { isValid: false, error: "Fax number can only contain digits, spaces, +, -, and ()" };
        }
        
        // Check minimum length (at least 7 digits for a valid fax number)
        const digitCount = cleaned.replace(/[^\d]/g, '').length;
        if (digitCount > 0 && digitCount < 7) {
            return { isValid: false, error: "Fax number must contain at least 7 digits" };
        }
        
        // Check maximum digits (reasonable limit of 15 digits)
        if (digitCount > 15) {
            return { isValid: false, error: "Fax number cannot exceed 15 digits" };
        }
        
        return { isValid: true, error: "" };
    };

    /**
     * Handle fax number input with validation
     */
    const handleFaxNumberChange = (field, value) => {
        // Filter out invalid characters in real-time
        // Allow: digits, spaces, dashes, parentheses, and + sign
        const filteredValue = value.replace(/[^\d\s+\-()]/g, '');
        
        // Limit length to 20 characters
        const limitedValue = filteredValue.length > 20 ? filteredValue.slice(0, 20) : filteredValue;
        
        // Update form data
        setFormData(prev => ({
            ...prev,
            [field]: limitedValue
        }));
        
        // Validate and update field errors
        const validation = validateFaxNumber(limitedValue);
        setFieldErrors(prev => ({
            ...prev,
            [field]: validation.error
        }));
    };

    /**
     * Handle form input changes
     */
    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    /**
     * Handle file upload for cover page (single PDF only)
     */
    const handleCoverPageUpload = (files) => {
        const file = files[0];
        if (file && file.type === 'application/pdf') {
            setCoverPageFile(file);
            // Don't overwrite pdfUrl if template is selected
            // Just store the file, it will be shown in the viewer via uploadedFiles array
        } else {
            message.error('Only PDF files are allowed for cover page');
        }
    };

    /**
     * Handle file upload for template (single PDF only)
     */
    const handleTemplateUpload = (files) => {
        const file = files[0];
        if (file && file.type === 'application/pdf') {
            setTemplateFile(file);
            setCurrentPdf(file);
            setPdfUrl(URL.createObjectURL(file));
        } else {
            message.error('Only PDF files are allowed for template');
        }
    };

    /**
     * Handle file upload for medical records (multiple PDFs)
     */
    const handleMedicalRecordsUpload = (files) => {
        const pdfFiles = Array.from(files).filter(file => file.type === 'application/pdf');
        if (pdfFiles.length === files.length) {
            setMedicalRecordsFiles(prev => [...prev, ...pdfFiles]);
        } else {
            message.error('Only PDF files are allowed for medical records');
        }
    };

    /**
     * Remove medical record file
     */
    const removeMedicalRecordFile = (index) => {
        setMedicalRecordsFiles(prev => prev.filter((_, i) => i !== index));
    };

    /**
     * Handle template selection from modal
     */
    const handleTemplateSelect = async (templateData) => {
        try {
            setLoading(true);
            setSelectedTemplate(templateData);
            setShowTemplateModal(false);
            
            // Get signed URL for the template file using dropdown selections
            const signedUrlResponse = await FaxAgentService.getTemplateSignedUrl({
                branch: templateData.branch,
                usecase: templateData.usecase,
                subusecase: templateData.subusecase,
                filename: templateData.filename
            });
            
            // Handle different response formats:
            // 1. String (signed URL directly)
            // 2. Object with response.signed_url
            // 3. Object with signed_url property
            let signedUrl = null;
            if (typeof signedUrlResponse === 'string') {
                signedUrl = signedUrlResponse;
            } else if (signedUrlResponse?.response?.signed_url) {
                signedUrl = signedUrlResponse.response.signed_url;
            } else if (signedUrlResponse?.signed_url) {
                signedUrl = signedUrlResponse.signed_url;
            } else if (signedUrlResponse?.signedUrl) {
                signedUrl = signedUrlResponse.signedUrl;
            } else if (signedUrlResponse?.url) {
                signedUrl = signedUrlResponse.url;
            }
            
            if (!signedUrl) {
                console.error('❌ Failed to extract signed URL from response:', signedUrlResponse);
                throw new Error('Failed to get signed URL for template');
            }
            
            console.log('✅ Extracted signed URL:', signedUrl);
            
            // Download the file from the signed URL and convert to File object
            // If CORS fails, fallback to backend proxy endpoint
            let templateFileObj = null;
            let downloadUrl = signedUrl;
            
            try {
                const response = await fetch(signedUrl, {
                    method: 'GET',
                    mode: 'cors', // Explicitly set CORS mode
                });
                
                if (!response.ok) {
                    throw new Error(`Failed to download template: ${response.statusText}`);
                }
                
                const blob = await response.blob();
                
                // Convert blob to File object
                templateFileObj = new File(
                    [blob],
                    templateData.name || templateData.filename,
                    { type: templateData.type || blob.type || 'application/pdf' }
                );
                
                console.log('✅ Template file downloaded directly from GCS:', templateFileObj);
            } catch (fetchError) {
                console.warn('⚠️ Direct download failed, trying backend proxy:', fetchError);
                
                // If CORS error or fetch fails, try backend proxy endpoint
                if (fetchError.message.includes('CORS') || fetchError.message.includes('fetch') || fetchError.name === 'TypeError') {
                    try {
                        console.log('🔄 Attempting download via backend proxy endpoint');
                        
                        // Use service layer to download via backend proxy (includes auth automatically)
                        const blob = await FaxAgentService.downloadTemplateFile({
                            branch: templateData.branch,
                            usecase: templateData.usecase,
                            subusecase: templateData.subusecase,
                            filename: templateData.filename
                        });
                        
                        // Convert blob to File object
                        templateFileObj = new File(
                            [blob],
                            templateData.name || templateData.filename,
                            { type: templateData.type || blob.type || 'application/pdf' }
                        );
                        
                        // Create blob URL for PDF viewer (avoids second unauthenticated request)
                        downloadUrl = URL.createObjectURL(blob);
                        
                        console.log('✅ Template file downloaded via backend proxy:', templateFileObj);
                    } catch (proxyError) {
                        console.error('❌ Backend proxy download also failed:', proxyError);
                        message.error('Failed to download template. Please try uploading manually or contact support.');
                        
                        // Still set the URL for viewing even if download fails
                        const pdfViewerFileObj = {
                            name: templateData.name,
                            size: templateData.size,
                            type: templateData.type,
                            url: signedUrl
                        };
                        setCurrentPdf(pdfViewerFileObj);
                        setPdfUrl(signedUrl);
                        return; // Exit early if both methods fail
                    }
                } else {
                    message.error('Failed to download template file. Please try again.');
                    return;
                }
            }
            
            // Store the File object for sending
            if (templateFileObj) {
                setTemplateFile(templateFileObj);
                
                // Create a file object from the template data for PDF viewer
                const pdfViewerFileObj = {
                    name: templateData.name,
                    size: templateData.size || templateFileObj.size,
                    type: templateData.type || templateFileObj.type,
                    url: downloadUrl
                };
                
                setCurrentPdf(pdfViewerFileObj);
                setPdfUrl(downloadUrl);
            }
        } catch (error) {
            console.error('Error getting template signed URL:', error);
            message.error(error.message || 'Failed to load template. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    /**
     * View PDF file
     */
    const viewPdfFile = (file) => {
        setCurrentPdf(file);
        setPdfUrl(URL.createObjectURL(file));
    };

    /**
     * Send fax with validation
     */
    const handleSendFax = async () => {
        try {
            setLoading(true);

            // Validate required fields
            if (!formData.toNumber.trim()) {
                message.error('Please enter recipient fax number');
                setFieldErrors(prev => ({ ...prev, toNumber: "Recipient fax number is required" }));
                return;
            }
            
            // Validate fax number formats
            const toNumberValidation = validateFaxNumber(formData.toNumber);
            if (!toNumberValidation.isValid) {
                setFieldErrors(prev => ({ ...prev, toNumber: toNumberValidation.error }));
                message.error(toNumberValidation.error);
                return;
            }
            
            // Validate from number is selected
            if (!formData.fromNumber) {
                message.error('Please select a From Number');
                return;
            }
            
            // Check minimum digit count for toNumber
            const toNumberDigits = formData.toNumber.replace(/[^\d]/g, '').length;
            if (toNumberDigits < 7) {
                setFieldErrors(prev => ({ ...prev, toNumber: "Fax number must contain at least 7 digits" }));
                message.error('Recipient fax number must contain at least 7 digits');
                return;
            }
            
            if (!formData.patientName?.trim()) {
                message.error('Please enter a patient name');
                setFieldErrors(prev => ({ ...prev, patientName: "Patient name is required" }));
                return;
            }
            
            if (!formData.subject.trim()) {
                message.error('Please enter fax subject');
                return;
            }
            if (!coverPageFile) {
                message.error('Please upload a cover page');
                return;
            }
            if (!templateFile && !pdfUrl) {
                message.error('Please upload or select a template');
                return;
            }

            // Get edited PDFs from PdfViewer (includes all annotations and edits)
            let editedPdfs = null;
            if (pdfViewerRef.current) {
                console.log('🔄 Getting edited PDFs from viewer...');
                editedPdfs = await pdfViewerRef.current.getEditedPdfs();
                console.log('✅ Got edited PDFs:', editedPdfs);
            }

            // Prepare payload with edited PDFs (if available) or original files
            // Only include templateFile if it's a File object (not a string)
            const templateFileToSend = editedPdfs?.template || templateFile;
            const payload = {
                coverpageFile: editedPdfs?.coverPage || coverPageFile,
                templateFile: templateFileToSend instanceof File ? templateFileToSend : (templateFileToSend || null),
                medicalRecordsFiles: editedPdfs?.medicalRecords?.length > 0 ? editedPdfs.medicalRecords : medicalRecordsFiles,
                toNumber: formData.toNumber,
                fromNumber: formData.fromNumber, // Selected from purchased numbers dropdown
                // customerId: sessionStorage.getItem('customerId'),
                emailId: formData.emailId || 'user@example.com', // TODO: Get from user context
                subject: formData.subject,
                description: formData.description,
                patientName: formData.patientName.trim()
            };

            console.log('📤 Sending fax with payload:', payload);

            // Call send fax API
            const result = await FaxAgentService.sendFax(payload);
            console.log('✅ Fax sent successfully:', result);

            message.success(result?.message || 'Fax sent successfully!');
            onSuccess();
            onClose();

        } catch (error) {
            console.error('❌ Error sending fax:', error);
            message.error(error.message || 'Failed to send fax. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Save as draft
     */
    const handleSaveAsDraft = () => {
        // TODO: Implement draft saving
        console.log('📝 Saving fax as draft');
    };

    /**
     * Drag and drop handlers
     */
    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDrop = (e, uploadHandler) => {
        e.preventDefault();
        uploadHandler(e.dataTransfer.files);
    };

    return (
        <>
            {/* Enhanced Send Fax Panel */}
            <div
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100vw",
                    height: "100vh",
                    backgroundColor: themeMode === 'dark' ? "#151138" : "#000",
                    zIndex: 2000,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {/* Main Panel */}
                <div
                    style={{
                        width: "80vw",
                        height: "85vh",
                        backgroundColor: themeMode === 'dark' ? "#04002b" : "#f5f5f5",
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: "12px",
                        position: "relative"
                    }}
                >
                    {/* Header */}
                    <div
                        style={{
                            padding: "24px",
                            borderBottom: `1px solid ${themeMode === 'dark' ? "#302d4c" : "#e0e0e0"}`,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <h1 style={{ 
                            margin: 0, 
                            fontSize: "24px", 
                            fontWeight: 600, 
                            color: themeMode === 'dark' ? "#fff" : "#000" 
                        }}>
                            Send Fax
                        </h1>
                        <CloseIcon
                            onClick={onClose}
                            style={{ 
                                color: themeMode === 'dark' ? "#fff" : "#000", 
                                cursor: "pointer", 
                                fontSize: "24px" 
                            }}
                        />
                    </div>

                    {/* Main Content */}
                    <div style={{ flex: 1, display: "flex", overflow: "hidden", position: "relative" }}>
                        {/* Left Panel - Form */}
                        <div
                            style={{
                                width: "40%",
                                padding: "20px 40px 20px 40px",
                                display: "flex",
                                flexDirection: "column",
                                gap: "12px",
                                overflowY: "auto",
                            }}
                        >

                            {/* To Fax Number */}
                            <div>
                                <TextField
                                    variant="standard"
                                    fullWidth
                                    placeholder="To Fax Number"
                                    value={formData.toNumber}
                                    onChange={(e) => handleFaxNumberChange("toNumber", e.target.value)}
                                    error={!!fieldErrors.toNumber}
                                    InputLabelProps={{
                                        shrink: true,
                                        style: { color: themeMode === 'dark' ? "#8B8FA8" : "#666666" },
                                    }}
                                    InputProps={{
                                        style: {
                                            color: themeMode === 'dark' ? "#fff" : "#000",
                                            borderBottom: `1px solid ${fieldErrors.toNumber 
                                                ? (themeMode === 'dark' ? "#ff6b6b" : "#d32f2f")
                                                : (themeMode === 'dark' ? "#2D2D5A" : "#e0e0e0")}`,
                                        },
                                    }}
                                />
                                {fieldErrors.toNumber && (
                                    <Typography
                                        sx={{
                                            color: themeMode === 'dark' ? "#ff6b6b" : "#d32f2f",
                                            fontSize: "12px",
                                            marginTop: "4px",
                                            marginLeft: "14px",
                                        }}
                                    >
                                        {fieldErrors.toNumber}
                                    </Typography>
                                )}
                            </div>

                            {/* From Number and Email */}
                            <div style={{ display: "flex", gap: "24px" }}>
                                <div style={{ flex: 1 }}>
                                    <Select
                                        variant="standard"
                                        fullWidth
                                        displayEmpty
                                        value={formData.fromNumber}
                                        onChange={(e) => handleInputChange("fromNumber", e.target.value)}
                                        renderValue={(selected) => {
                                            if (!selected) {
                                                return <span style={{ color: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.42)' }}>From Number</span>;
                                            }
                                            // Find the selected number and format it for display
                                            const selectedNum = purchasedNumbers.find(n => (n?.faxNumber || n?.fax_number || n?.phoneNumber) === selected);
                                            const formattedNumber = formatPhoneNumber(selected);
                                            return selectedNum?.facilityName 
                                                ? `${formattedNumber} (${selectedNum.facilityName})`
                                                : formattedNumber;
                                        }}
                                        sx={{
                                            color: themeMode === 'dark' ? "#fff" : "#000",
                                            fontSize: '16px',
                                            '&:before': {
                                                borderBottom: `1px solid ${themeMode === 'dark' ? "#2D2D5A" : "#e0e0e0"}`,
                                            },
                                            '&:after': {
                                                borderBottom: `1px solid ${themeMode === 'dark' ? "#2D2D5A" : "#e0e0e0"}`,
                                            },
                                            '&:hover:not(.Mui-disabled):before': {
                                                borderBottom: `1px solid ${themeMode === 'dark' ? "#4a4867" : "#b0b0b0"}`,
                                            },
                                            '.MuiSelect-icon': {
                                                color: themeMode === 'dark' ? "#8B8FA8" : "#666666",
                                            },
                                            '.MuiSelect-select': {
                                                paddingTop: '4px',
                                                paddingBottom: '5px',
                                            },
                                        }}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    backgroundColor: themeMode === 'dark' ? "#1B1A33" : "#ffffff",
                                                    border: `1px solid ${themeMode === 'dark' ? "#302d4c" : "#e0e0e0"}`,
                                                }
                                            }
                                        }}
                                    >
                                        <MenuItem value="" disabled sx={{ color: themeMode === 'dark' ? "#8B8FA8" : "#666666" }}>
                                            {loadingNumbers ? "Loading..." : "From Number"}
                                        </MenuItem>
                                        {purchasedNumbers.map((num, index) => {
                                            // Handle the object structure: { faxNumber, facilityName, ... }
                                            const faxNumber = num?.faxNumber || num?.fax_number || num?.phoneNumber || '';
                                            const formattedNumber = formatPhoneNumber(faxNumber);
                                            const displayName = num?.facilityName 
                                                ? `${formattedNumber} (${num.facilityName})`
                                                : formattedNumber;
                                            
                                            return (
                                                <MenuItem 
                                                    key={num?.numberId || faxNumber || index} 
                                                    value={faxNumber}
                                                    sx={{
                                                        color: themeMode === 'dark' ? "#fff" : "#000",
                                                        '&:hover': {
                                                            backgroundColor: themeMode === 'dark' ? "#252144" : "#f5f5f5",
                                                        },
                                                        '&.Mui-selected': {
                                                            backgroundColor: themeMode === 'dark' ? "#302d4c" : "#e0e0e0",
                                                        },
                                                    }}
                                                >
                                                    {displayName}
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <TextField
                                        variant="standard"
                                        fullWidth
                                        placeholder="Email"
                                        value={formData.emailId}
                                        onChange={(e) => handleInputChange("emailId", e.target.value)}
                                        InputLabelProps={{ shrink: true, style: { color: themeMode === 'dark' ? "#8B8FA8" : "#666666" } }}
                                        InputProps={{ style: { color: themeMode === 'dark' ? "#fff" : "#000", borderBottom: `1px solid ${themeMode === 'dark' ? "#2D2D5A" : "#e0e0e0"}` } }}
                                    />
                                </div>
                            </div>

                            {/* Patient Name */}
                            <div>
                                <TextField
                                    variant="standard"
                                    fullWidth
                                    placeholder="Patient Name *"
                                    value={formData.patientName}
                                    onChange={(e) => {
                                        handleInputChange("patientName", e.target.value);
                                        // Clear error when user starts typing
                                        if (fieldErrors.patientName) {
                                            setFieldErrors(prev => ({ ...prev, patientName: "" }));
                                        }
                                    }}
                                    error={!!fieldErrors.patientName}
                                    required
                                    InputLabelProps={{ shrink: true, style: { color: themeMode === 'dark' ? "#8B8FA8" : "#666666" } }}
                                    InputProps={{ 
                                        style: { 
                                            color: themeMode === 'dark' ? "#fff" : "#000", 
                                            borderBottom: `1px solid ${fieldErrors.patientName 
                                                ? (themeMode === 'dark' ? "#ff6b6b" : "#d32f2f")
                                                : (themeMode === 'dark' ? "#2D2D5A" : "#e0e0e0")}` 
                                        } 
                                    }}
                                />
                                {fieldErrors.patientName && (
                                    <Typography
                                        sx={{
                                            color: themeMode === 'dark' ? "#ff6b6b" : "#d32f2f",
                                            fontSize: "12px",
                                            marginTop: "4px",
                                            marginLeft: "14px",
                                        }}
                                    >
                                        {fieldErrors.patientName}
                                    </Typography>
                                )}
                            </div>

                            {/* Subject */}
                            <TextField
                                variant="standard"
                                fullWidth
                                placeholder="Subject"
                                value={formData.subject}
                                onChange={(e) => handleInputChange("subject", e.target.value)}
                                InputLabelProps={{ shrink: true, style: { color: themeMode === 'dark' ? "#8B8FA8" : "#666666" } }}
                                InputProps={{ style: { color: themeMode === 'dark' ? "#fff" : "#000", borderBottom: `1px solid ${themeMode === 'dark' ? "#2D2D5A" : "#e0e0e0"}` } }}
                            />

                            {/* Choose Template Button */}
                            <button
                                onClick={() => setShowTemplateModal(true)}
                                style={{
                                    width: "100%",
                                    border: "none",
                                    borderBottom: `1px solid ${themeMode === 'dark' ? "#2D2D5A" : "#e0e0e0"}`,
                                    background: "transparent",
                                    color: themeMode === 'dark' ? "#fff" : "#000",
                                    fontSize: "14px",
                                    padding: "6px 0",
                                    cursor: "pointer",
                                    textAlign: "left",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                }}
                            >
                                <span>Choose a Template</span>
                            </button>

                            {/* Selected Template Display */}
                            {selectedTemplate && (
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        padding: "8px 12px",
                                        backgroundColor: themeMode === 'dark' ? "rgba(42, 43, 74, 0.8)" : "rgba(245, 245, 245, 0.8)",
                                        borderRadius: "6px",
                                        border: `1px solid ${themeMode === 'dark' ? "#302d4c" : "#e0e0e0"}`,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: themeMode === 'dark' ? "#fff" : "#000",
                                            fontSize: "13px",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            flex: 1,
                                        }}
                                    >
                                        {selectedTemplate.name || selectedTemplate.filename || "Selected Template"}
                                    </Typography>
                                    <DeleteIcon
                                        onClick={handleRemoveTemplate}
                                        style={{ 
                                            fontSize: "18px", 
                                            color: themeMode === 'dark' ? "#ff6b6b" : "#d32f2f", 
                                            cursor: "pointer", 
                                            marginLeft: "8px" 
                                        }}
                                    />
                                </div>
                            )}

                            {/* Write here text area */}
                            <Box sx={{ flex: "0 0 40%", maxWidth: "100%", display: "flex", flexDirection: "column" }}>
                                <TextField
                                    multiline
                                    rows={2}
                                    placeholder="Add Description..."
                                    fullWidth
                                    value={formData.description}
                                    onChange={(e) => handleInputChange("description", e.target.value)}
                                    sx={{
                                        flex: 1,
                                        "& .MuiOutlinedInput-root": {
                                            // backgroundColor: themeMode === 'dark' ? "#1B1A33" : "#f5f5f5",
                                            borderRadius: 2,
                                            "& fieldset": { border: "none" },
                                            "&:hover": { borderColor: themeMode === 'dark' ? "#4a4867" : "#b0b0b0" },
                                        },
                                        "& .MuiOutlinedInput-input": { color: themeMode === 'dark' ? "#fff" : "#000", padding: "12px 14px" }
                                    }}
                                />
                            </Box>

                            {/* File Upload Sections */}
                            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                                {/* Cover Page Upload */}
                                {/* <div>
                                    <Typography
                                        sx={{
                                            color: themeMode === 'dark' ? "#8B8FA8" : "#666666",
                                            fontSize: "12px",
                                            fontWeight: 500,
                                            marginBottom: "6px",
                                        }}
                                    >
                                        Upload Cover Page (.pdf)
                                    </Typography>
                                </div> */}
                                    <div
                                        onDragOver={handleDragOver}
                                        onDrop={(e) => handleDrop(e, handleCoverPageUpload)}
                                        onClick={() => coverPageInputRef.current?.click()}
                                        style={{
                                            border: `2px dashed ${themeMode === 'dark' ? "#3D3E5F" : "#b0b0b0"}`,
                                            borderRadius: "8px",
                                            height: "50px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            cursor: "pointer",
                                            // backgroundColor: themeMode === 'dark' ? "rgba(42, 43, 74, 0.3)" : "rgba(245, 245, 245, 0.3)",
                                            position: "relative",
                                        }}
                                    >
                                        <input
                                            ref={coverPageInputRef}
                                            type="file"
                                            accept=".pdf"
                                            onChange={(e) => handleCoverPageUpload(e.target.files)}
                                            style={{ display: "none" }}
                                        />
                                        <UploadIcon style={{ fontSize: "18px", color: themeMode === 'dark' ? "#8B8FA8" : "#666666", marginRight: "6px" }} />
                                        <Typography sx={{ color: themeMode === 'dark' ? "#8B8FA8" : "#666666", fontSize: "12px" }}>
                                            {coverPageFile ? coverPageFile.name : "Click to upload cover page(.pdf)"}
                                        </Typography>
                                        {coverPageFile && (
                                            <DeleteIcon
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setCoverPageFile(null);
                                                }}
                                                style={{ 
                                                    fontSize: "20px", 
                                                    color: themeMode === 'dark' ? "#ff6b6b" : "#d32f2f", 
                                                    cursor: "pointer", 
                                                    marginLeft: "8px" 
                                                }}
                                            />
                                        )}
                                    </div>

                                {/* Template Upload */}
                                {/* <div>
                                    <Typography
                                        sx={{
                                            color: themeMode === 'dark' ? "#8B8FA8" : "#666666",
                                            fontSize: "12px",
                                            fontWeight: 500,
                                            marginBottom: "6px",
                                        }}
                                    >
                                        Upload Template (.pdf)
                                    </Typography>
                                    <div
                                        onDragOver={handleDragOver}
                                        onDrop={(e) => handleDrop(e, handleTemplateUpload)}
                                        onClick={() => templateInputRef.current?.click()}
                                        style={{
                                            border: `2px dashed ${themeMode === 'dark' ? "#3D3E5F" : "#b0b0b0"}`,
                                            borderRadius: "8px",
                                            height: "50px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            cursor: "pointer",
                                            // backgroundColor: themeMode === 'dark' ? "rgba(42, 43, 74, 0.3)" : "rgba(245, 245, 245, 0.3)",
                                            position: "relative",
                                        }}
                                    >
                                        <input
                                            ref={templateInputRef}
                                            type="file"
                                            accept=".pdf"
                                            onChange={(e) => handleTemplateUpload(e.target.files)}
                                            style={{ display: "none" }}
                                        />
                                        <UploadIcon style={{ fontSize: "18px", color: themeMode === 'dark' ? "#8B8FA8" : "#666666", marginRight: "6px" }} />
                                        <Typography sx={{ color: themeMode === 'dark' ? "#8B8FA8" : "#666666", fontSize: "12px" }}>
                                            {templateFile ? templateFile.name : "Click to upload template"}
                                        </Typography>
                                        {templateFile && (
                                            <DeleteIcon
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setTemplateFile(null);
                                                }}
                                                style={{ 
                                                    fontSize: "20px", 
                                                    color: themeMode === 'dark' ? "#ff6b6b" : "#d32f2f", 
                                                    cursor: "pointer", 
                                                    marginLeft: "8px" 
                                                }}
                                            />
                                        )}
                                    </div>
                                </div> */}

                                {/* Medical Records Upload */}
                                {/* <div>
                                    <Typography
                                        sx={{
                                            color: themeMode === 'dark' ? "#8B8FA8" : "#666666",
                                            fontSize: "12px",
                                            fontWeight: 500,
                                            marginBottom: "6px",
                                        }}
                                    >
                                        Upload files (.pdf) - Multiple files allowed
                                    </Typography>
                                </div> */}
                                    <div
                                        onDragOver={handleDragOver}
                                        onDrop={(e) => handleDrop(e, handleMedicalRecordsUpload)}
                                        onClick={() => medicalRecordsInputRef.current?.click()}
                                        style={{
                                            border: `2px dashed ${themeMode === 'dark' ? "#3D3E5F" : "#b0b0b0"}`,
                                            borderRadius: "8px",
                                            minHeight: "60px",
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            cursor: "pointer",
                                            // backgroundColor: themeMode === 'dark' ? "rgba(42, 43, 74, 0.3)" : "rgba(245, 245, 245, 0.3)",
                                            padding: "12px",
                                        }}
                                    >
                                        <input
                                            ref={medicalRecordsInputRef}
                                            type="file"
                                            accept=".pdf"
                                            multiple
                                            onChange={(e) => handleMedicalRecordsUpload(e.target.files)}
                                            style={{ display: "none" }}
                                        />
                                        <UploadIcon style={{ fontSize: "18px", color: themeMode === 'dark' ? "#8B8FA8" : "#666666", marginBottom: "6px" }} />
                                        <Typography sx={{ color: themeMode === 'dark' ? "#8B8FA8" : "#666666", fontSize: "12px", textAlign: "center" }}>
                                            Click to upload medical records (.pdf) - Multiple files allowed
                                        </Typography>
                                        
                                        {/* Uploaded files list */}
                                        {medicalRecordsFiles.length > 0 && (
                                            <div style={{ width: "100%", marginTop: "12px" }}>
                                                {medicalRecordsFiles.map((file, index) => (
                                                    <div
                                                        key={index}
                                                        style={{
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            alignItems: "center",
                                                            padding: "8px 12px",
                                                            marginBottom: "4px",
                                                            backgroundColor: themeMode === 'dark' ? "rgba(42, 43, 74, 0.8)" : "rgba(245, 245, 245, 0.8)",
                                                            borderRadius: "6px",
                                                            border: `1px solid ${themeMode === 'dark' ? "#302d4c" : "#e0e0e0"}`,
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                color: themeMode === 'dark' ? "#fff" : "#000",
                                                                fontSize: "13px",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                whiteSpace: "nowrap",
                                                                flex: 1,
                                                            }}
                                                        >
                                                            {file.name}
                                                        </Typography>
                                                        <DeleteIcon
                                                            onClick={() => removeMedicalRecordFile(index)}
                                                            style={{ 
                                                                fontSize: "18px", 
                                                                color: themeMode === 'dark' ? "#ff6b6b" : "#d32f2f", 
                                                                cursor: "pointer", 
                                                                marginLeft: "8px" 
                                                            }}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                            </div>
                        </div>

                        {/* Right Panel - PDF Viewer */}
                        <>
                            {/* Fullscreen overlay backdrop - only visible when fullscreen */}
                            {isPdfFullScreen && (
                                <Box
                                    sx={{
                                        position: 'fixed',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        width: '100vw',
                                        height: '100vh',
                                        zIndex: 1300,
                                        backgroundColor: themeMode === 'dark' ? "#1B1A33" : "#f5f5f5",
                                        display: 'flex',
                                        flexDirection: 'column',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Box sx={{ 
                                        flexShrink: 0, 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center', 
                                        p: 1, 
                                        pl: 10,
                                        borderBottom: `1px solid ${themeMode === 'dark' ? "#302d4c" : "#e0e0e0"}`,
                                        backgroundColor: themeMode === 'dark' ? "#252144" : "#ffffff"
                                    }}>
                                        <Typography sx={{ 
                                            fontSize: 14, 
                                            fontWeight: 600, 
                                            color: themeMode === 'dark' ? "#fff" : "#000"
                                        }}>
                                            PDF Editor — Full screen
                                        </Typography>
                                        <Tooltip title="Exit full screen">
                                            <IconButton 
                                                size="small" 
                                                onClick={() => setIsPdfFullScreen(false)} 
                                                sx={{ color: themeMode === 'dark' ? "#fff" : "#000" }}
                                                aria-label="Exit full screen"
                                            >
                                                <FullscreenExitIcon sx={{ fontSize: 20 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                    <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }} />
                                </Box>
                            )}
                            
                            {/* PDF Viewer Container */}
                            <div
                                ref={pdfViewerContainerRef}
                                style={isPdfFullScreen ? {
                                    position: 'fixed',
                                    top: '48px',
                                    left: '72px',
                                    right: 0,
                                    bottom: 0,
                                    width: 'calc(100vw - 72px)',
                                    height: 'calc(100vh - 48px)',
                                    zIndex: 1301,
                                    backgroundColor: themeMode === 'dark' ? "#1B1A33" : "#f5f5f5",
                                    display: "flex",
                                    flexDirection: "column",
                                    padding: "0",
                                    boxSizing: "border-box",
                                    overflow: "hidden",
                                } : {
                                    width: "60%",
                                    borderLeft: `1px solid ${themeMode === 'dark' ? "#302d4c" : "#e0e0e0"}`,
                                    display: "flex",
                                    flexDirection: "column",
                                    padding: "16px 20px 72px 16px",
                                    boxSizing: "border-box",
                                    overflow: "hidden",
                                }}
                            >
                                {!isPdfFullScreen && (
                                    <Box sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'flex-end', 
                                        py: 0.5, 
                                        px: 1, 
                                        borderBottom: `1px solid ${themeMode === 'dark' ? "#302d4c" : "#e0e0e0"}`,
                                        marginBottom: 1
                                    }}>
                                        <Tooltip title="Full screen">
                                            <IconButton 
                                                size="small" 
                                                onClick={() => setIsPdfFullScreen(true)} 
                                                sx={{ color: themeMode === 'dark' ? "#8B8FA8" : "#666666" }}
                                            >
                                                <FullscreenIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                )}
                                <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                                    <PdfViewer
                                        ref={pdfViewerRef}
                                        pdfUrl={pdfUrl}
                                        fileName={currentPdf?.name}
                                        themeMode={themeMode}
                                        coverPageFile={coverPageFile}
                                        templateFile={templateFile}
                                        medicalRecordsFiles={medicalRecordsFiles}
                                        isFullscreen={isPdfFullScreen}
                                    />
                                </Box>
                            </div>
                        </>
                    </div>

                    {/* Footer - Send Button */}
                    <div style={{ 
                        position: "absolute", 
                        bottom: "24px", 
                        right: "24px",
                        zIndex: 10
                    }}>
                        <button
                            onClick={handleSendFax}
                            disabled={loading}
                            style={{
                                height: "40px",
                                backgroundColor: "#FF6B47",
                                border: "1px solid #FF6B47",
                                borderRadius: "8px",
                                color: "#fff",
                                cursor: "pointer",
                                fontSize: "14px",
                                padding: "0 22px",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                width: "auto",
                                minWidth: "120px",
                            }}
                        >
                            <SendIcon style={{ fontSize: "16px" }} />
                            {loading ? "Sending..." : "Send Fax"}
                        </button>
                    </div>
                </div>
            </div>

            {/* Template Selection Modal */}
            {showTemplateModal && (
                <TemplateSelectionModal
                    onClose={() => setShowTemplateModal(false)}
                    onSelectTemplate={handleTemplateSelect}
                    themeMode={themeMode}
                />
            )}
        </>
    );
};

export default EnhancedSendFaxPanel;